import { Button } from '@readcloud/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@readcloud/ui/dialog';
import { Progress } from '@readcloud/ui/progress';
import { authSelectors } from '@readcloud/state';
import * as React from 'react';
import { useSelector } from 'react-redux';

// Check if running in Tauri environment
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

// Mirrors UpdateInfo in src-tauri/src/updater.rs
interface UpdateInfo {
  available: boolean;
  version: string | null;
  current_version: string;
  notes: string | null;
  pub_date: string | null;
  mandatory: boolean;
}

// Mirrors the `update-progress` event payload in src-tauri/src/updater.rs
interface UpdateProgress {
  downloaded: number;
  total: number | null;
  percentage: number;
}

const INITIAL_CHECK_DELAY_MS = 10_000;
const POST_LOGIN_CHECK_DELAY_MS = 5_000;
const RECHECK_INTERVAL_MS = 4 * 60 * 60 * 1000;

type Phase = 'idle' | 'prompt' | 'downloading' | 'installing' | 'error';

const formatMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

/**
 * Desktop in-app updater UI. Checks for updates shortly after launch, again
 * shortly after login, and every few hours after that; prompts the user, and
 * shows download progress while the Rust side fetches, verifies, and installs.
 * On Windows the app exits into the installer; on macOS/Linux the updater
 * plugin restarts the app itself — either way this component never sees a
 * successful install "complete".
 *
 * Pre-login checks carry no token, so the release endpoint only serves
 * break-glass releases (fixes that must reach users who can't log in).
 * Post-login checks send the access token and get staged-rollout offers for
 * the user's school. A `mandatory` release hides the "Later" button.
 *
 * On mobile (and in the plain web build) this renders nothing: the web build
 * has no __TAURI__, and the mobile Rust stub always reports no update.
 */
export const AppUpdater: React.FC = () => {
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [update, setUpdate] = React.useState<UpdateInfo | null>(null);
  const [progress, setProgress] = React.useState<UpdateProgress | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const token = useSelector(authSelectors.getAccessToken) as string | undefined;
  const tokenRef = React.useRef<string | undefined>(token);
  tokenRef.current = token;

  // Version the user clicked "Later" on — don't re-prompt for it this session.
  const dismissedVersionRef = React.useRef<string | null>(null);
  const phaseRef = React.useRef<Phase>('idle');
  phaseRef.current = phase;

  const check = React.useCallback(async () => {
    // Don't interrupt an in-flight prompt/download with a fresh check
    if (phaseRef.current !== 'idle') return;
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const info = await invoke<UpdateInfo>('updater_check', {
        token: tokenRef.current ?? null,
      });
      if (phaseRef.current !== 'idle') return;
      if (
        info.available &&
        info.version &&
        (info.mandatory || info.version !== dismissedVersionRef.current)
      ) {
        setUpdate(info);
        setPhase('prompt');
      }
    } catch (err) {
      // Expected offline / behind captive portals — never nag the user
      console.warn('Update check failed:', err);
    }
  }, []);

  // Launch check (tokenless unless already logged in) + periodic re-checks.
  React.useEffect(() => {
    if (!isTauri) return;
    const initialTimer = setTimeout(check, INITIAL_CHECK_DELAY_MS);
    const recheckTimer = setInterval(check, RECHECK_INTERVAL_MS);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(recheckTimer);
    };
  }, [check]);

  // Re-check shortly after login: with the token the endpoint can offer
  // staged-rollout releases for the user's school.
  React.useEffect(() => {
    if (!isTauri || !token) return;
    const timer = setTimeout(check, POST_LOGIN_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [token, check]);

  const install = React.useCallback(async () => {
    setPhase('downloading');
    setProgress(null);
    setError(null);

    const { invoke } = await import('@tauri-apps/api/core');
    const { listen } = await import('@tauri-apps/api/event');

    const unlisten = await listen<UpdateProgress>('update-progress', (event) => {
      setProgress(event.payload);
    });

    try {
      // On success this never really "returns" to a running app: Windows exits
      // into the installer, macOS/Linux restart. Anything after the await is
      // only reached in the brief window before the process goes away.
      await invoke('updater_download_and_install', {
        token: tokenRef.current ?? null,
      });
      setPhase('installing');
    } catch (err) {
      setError(typeof err === 'string' ? err : String(err));
      setPhase('error');
    } finally {
      unlisten();
    }
  }, []);

  const dismiss = React.useCallback(() => {
    if (update?.version) {
      dismissedVersionRef.current = update.version;
    }
    setPhase('idle');
    setUpdate(null);
    setProgress(null);
    setError(null);
  }, [update]);

  if (!isTauri || phase === 'idle' || !update) return null;

  const busy = phase === 'downloading' || phase === 'installing';
  const downloadComplete =
    phase === 'installing' ||
    (progress !== null && progress.percentage >= 100);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // No dismissing once the download has started, or for forced updates
        if (!open && !busy && !update.mandatory) dismiss();
      }}
    >
      <DialogContent
        className="rc-max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {update.mandatory ? 'Update required' : 'Update available'}
          </DialogTitle>
          <DialogDescription>
            Version {update.version} is available — you have{' '}
            {update.current_version}.
            {update.mandatory &&
              ' This update is required to continue using the app.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'prompt' && update.notes && (
          <div className="rc-max-h-40 rc-overflow-y-auto rc-whitespace-pre-wrap rc-rounded-md rc-bg-muted rc-p-3 rc-text-sm">
            {update.notes}
          </div>
        )}

        {busy && (
          <div className="rc-space-y-2">
            <Progress value={progress?.percentage ?? 0} />
            <p className="rc-text-sm rc-text-muted-foreground">
              {downloadComplete
                ? 'Verifying and installing… the app will restart.'
                : progress?.total
                ? `Downloading… ${formatMB(progress.downloaded)} MB of ${formatMB(
                    progress.total
                  )} MB`
                : 'Downloading…'}
            </p>
          </div>
        )}

        {phase === 'error' && (
          <p className="rc-text-sm rc-text-destructive rc-break-words">
            The update could not be installed: {error}
          </p>
        )}

        <DialogFooter>
          {phase === 'prompt' && (
            <>
              {!update.mandatory && (
                <Button variant="outline" onClick={dismiss}>
                  Later
                </Button>
              )}
              <Button onClick={install}>Install now</Button>
            </>
          )}
          {phase === 'error' && (
            <>
              {!update.mandatory && (
                <Button variant="outline" onClick={dismiss}>
                  Close
                </Button>
              )}
              <Button onClick={install}>Try again</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppUpdater;
